/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import * as React from "react";
import {Component} from "react";
import {ColorMap} from "../../util/Color";
import SingleColorChooser from "./SingleColorChooser";
import {$t} from "../../lang/LangUtil";
import {Select, Theme} from "@ice/components";
import {IconInfo} from "../../util/IconInfo";
import {XIcon} from "../../ui/XIcon";


export interface ColorMapSelectionProps {
    keys: string[]
    colorMap: ColorMap;
    onChanged: (colorMap: ColorMap) => void;
}


export interface ColorMapSelectionState {
    colorMap: ColorMap;
    newColor: string;
}


export class ColorMapSelection extends Component<ColorMapSelectionProps, ColorMapSelectionState> {

    private bottomRef = React.createRef<HTMLDivElement>();

    constructor(props: ColorMapSelectionProps) {
        super(props);
        this.state = {
            colorMap: {...this.props.colorMap},
            newColor: this.props.colorMap[""] ?? "#0077dd"
        }
    }

    private renderColorSelectionLine = (key: string, color: string, deletable: boolean = true) => {
        return (
            <div key={key}
                 style={{display: "flex", flexDirection: "row", alignItems: "center", padding: "0.2rem 0"}}
            >
                <SingleColorChooser simpleSelect={true}
                                    color={color}
                                    onColorChanged={color => this.changeColor(key, color)}
                                    onEditFinished={() => this.props.onChanged(this.state.colorMap)}
                />
                <span className={"noselect"}>
                    {key !== "" ? key : <b>{$t("defaultcolor")}</b>}
                </span>
                <div style={{flexGrow: 1}}/>
                {
                    deletable &&
                    <div>
                        <XIcon
                            icon={IconInfo.DELETE}
                            iconSize={"1.2em"}
                            title={$t("delete")}
                            onClick={() => this.deleteColor(key)}
                            css={(theme: Theme) => css`
                              fill: ${theme.focusAndAccentColor};
                              cursor: pointer;
                              &:hover {
                                fill: ${theme.selectionColor};
                              }
                            `}
                        />
                    </div>
                }
            </div>
        )
    }

    private changeColor = (key: string, color: string) => {
        const newColorMap = {...this.state.colorMap};
        newColorMap[key] = color;
        this.setState({colorMap: newColorMap});
    }

    private deleteColor = (key: string) => {
        const newColorMap = {...this.state.colorMap};
        delete newColorMap[key];
        this.setState({colorMap: newColorMap});
        this.props.onChanged(newColorMap);
    }

    private addColor = (key: string) => {
        if (key) {
            const newColorMap = {...this.state.colorMap};
            newColorMap[key] = this.state.newColor;
            this.setState({colorMap: newColorMap});

            this.props.onChanged(newColorMap);

            // scroll to the bottom on add
            setTimeout(() => this.bottomRef.current?.scrollIntoView(), 0);
        }
    }

    render() {
        const selectableSet: Set<string> = new Set(this.props.keys);
        Object.keys(this.state.colorMap).forEach(key => selectableSet.delete(key));

        const selectableValues = Array.from(selectableSet);

        return (
            <div
                css={(theme: Theme) => css`
                  display: flex;
                  flex-direction: column;
                  width: 100%;
                  border: 1px solid ${theme.borderColor.normal};
                  border-radius: ${theme.borderRadius.normal}em;
                  padding: 0.2rem 0.4rem;
                `}
            >
                <div style={{display: "flex", flexDirection: "column", overflowY: "auto", maxHeight: "12rem"}}>
                    { this.renderColorSelectionLine("", this.state.colorMap[""] ?? "#ff0000", false) }
                    {Object.keys(this.state.colorMap).map((key) => (
                        key && key !== "" ? this.renderColorSelectionLine(key, this.state.colorMap[key]) : null
                    ))}
                    <div ref={this.bottomRef} />
                </div>

                {
                    selectableValues.length > 0 &&
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", padding: "0.2rem 0"}}>
                        <SingleColorChooser
                            simpleSelect={true}
                            color={this.state.newColor}
                            onColorChanged={(color) => {this.setState({newColor: color})}}
                        />
                        <span>
                            <Select<string>
                                clearable={false}
                                searchable={true}
                                minimal={false}
                                getValue={i => selectableValues[i]}
                                getOptionLabel={i => selectableValues[i]}
                                getSelectionLabel={def => def}
                                onChange={v => this.addColor(v as string)}
                                optionCount={selectableValues.length}
                                style={{width: "20em"}}
                                placeholder={$t("addvalue")}
                            />
                        </span>
                    </div>
                }
            </div>
        );
    }
}